// Global Instructions Rule Applied!

import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { useDrag } from 'react-dnd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { portalColors } from '../../../../../portal-configs/colors.js'
import { KanbanCardProps, KanbanItem } from '../../interfaces.ts'
import { getColumnColour } from '../../controller/parts/colorUtils.ts'
import { ITEM_TYPES_EXPORT } from './DropZone.tsx'

export function KanbanCard<T extends KanbanItem>({
  item,
  index,
  column,
  cardConfig,
  onMoveItem,
  onUpdateItem,
  onCardHeightChange,
  successfullyMovedCardId
}: KanbanCardProps<T>): React.ReactElement {
  const columnColor = getColumnColour(column)
  const isReadOnlyColumn = column.dropOption === false

  const ref = useRef<HTMLDivElement>(null)
  const [cardHeight, setCardHeight] = useState<number | null>(null)

  const itemId = cardConfig.getItemId(item)
  const shouldPulse = successfullyMovedCardId === itemId

  // Measure card height and notify parent
  useEffect(() => {
    if (ref.current) {
      const height = ref.current.offsetHeight
      setCardHeight(height)
      if (onCardHeightChange) {
        onCardHeightChange(cardConfig.getItemId(item), height)
      }
    }
  }, [item, onCardHeightChange, cardConfig])

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ITEM_TYPES_EXPORT.KANBAN_ITEM,
      item: {
        itemId: cardConfig.getItemId(item),
        columnId: column.id,
        index,
        cardHeight
      },
      canDrag: !isReadOnlyColumn,
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    }),
    [item, column, index, cardHeight, isReadOnlyColumn, cardConfig]
  )

  drag(ref)

  const handleEditClick = useCallback((): void => {
    if (cardConfig.onItemEdit) {
      cardConfig.onItemEdit(item)
    }
  }, [item, cardConfig])

  const title = cardConfig.getItemTitle(item)
  const titleDisplay = cardConfig.getItemTitleDisplay ? cardConfig.getItemTitleDisplay(item) : null
  const subtitle = cardConfig.getItemSubtitle ? cardConfig.getItemSubtitle(item) : null
  const itemColor = cardConfig.getItemColor ? cardConfig.getItemColor(item, column) : columnColor

  const renderContent = useMemo(() => {
    if (cardConfig.renderItemContent) {
      return cardConfig.renderItemContent(item, column)
    }
    return null
  }, [item, column, cardConfig])

  // Optional compact meta (e.g. age chips). When present, the header becomes a
  // two-row stack so the edit affordance sits top-right and the meta sits
  // flush-right on the subtitle row — title/subtitle stay tight (no dead space
  // under a short title) and a long title folds above the subtitle row.
  const meta = cardConfig.renderItemMeta ? cardConfig.renderItemMeta(item, column) : null

  const editButton = cardConfig.onItemEdit ? (
    <button
      type='button'
      aria-label='Edit'
      onClick={(e) => {
        e.stopPropagation()
        handleEditClick()
      }}
      className='flex-shrink-0 bg-transparent border-0 p-0 cursor-pointer text-gray-400 hover:text-orange-600 transition-colors'
      style={{ lineHeight: 0 }}
    >
      <FontAwesomeIcon icon={faEdit} className='text-sm' />
    </button>
  ) : null

  return (
    <div className='relative'>
      <div
        ref={ref}
        className={`relative p-3 mb-2 bg-white rounded-xl border transition-all duration-200 kanban-card ${
          isDragging ? 'opacity-60' : 'opacity-100'
        } ${isReadOnlyColumn ? 'cursor-default border-gray-300' : 'cursor-move hover:shadow-lg border-gray-200'} ${
          shouldPulse ? 'kanban-card-pulse' : ''
        }`}
        onDragStart={(e) => {
          const emptyImg = new Image()
          emptyImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs='
          e.dataTransfer.setDragImage(emptyImg, 0, 0)
        }}
      >
        {/* Card Header */}
        <div className='flex items-start gap-2 mb-1'>
          <div
            className='w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shadow-lg'
            style={{
              backgroundColor: itemColor,
              color: '#FFFFFF'
            }}
          >
            {title.substring(0, 2).toUpperCase()}
          </div>
          {meta ? (
            <div className='flex-1 min-w-0'>
              <div className='flex items-start justify-between gap-2'>
                <h4
                  className='text-sm font-semibold line-clamp-2 leading-tight flex-1 min-w-0'
                  style={{ color: portalColors.neutral[900] }}
                >
                  {titleDisplay ?? title}
                </h4>
                {editButton && <div className='flex-shrink-0'>{editButton}</div>}
              </div>
              <div className='flex items-center justify-between gap-2 mt-0.5'>
                <div className='text-xs truncate min-w-0' style={{ color: portalColors.neutral[500] }}>
                  {subtitle}
                </div>
                <div className='flex-shrink-0'>{meta}</div>
              </div>
            </div>
          ) : (
            <>
              <div className='flex-1 min-w-0'>
                <h4
                  className='text-sm font-semibold line-clamp-2 mb-1 leading-tight'
                  style={{ color: portalColors.neutral[900] }}
                >
                  {titleDisplay ?? title}
                </h4>
                {subtitle && (
                  <p className='text-xs' style={{ color: portalColors.neutral[500] }}>
                    {subtitle}
                  </p>
                )}
              </div>
              {editButton}
            </>
          )}
        </div>

        {/* Custom Content */}
        {renderContent && <div className='mb-2'>{renderContent}</div>}
      </div>
    </div>
  )
}
